import { useEffect, useRef, useState, type FormEvent } from 'react';
import { t } from '../i18n/idioma';
import { useSession } from '../state/SessionContext';

/** As classes de cada lugar onde a confirmação aparece: a tela de entrada e o cartão da landing aberta. */
const CLASSES = {
  tela: {
    form: 'pilha', campo: 'campo', rotulo: 'campo__rotulo', caixa: 'campo__caixa', botao: 'btn btn--primary',
    elo: 'elo', erro: 'aviso', nota: 'aviso aviso--status', miudo: 'footnote'
  },
  landing: {
    form: 'lp-gump__corpo', campo: 'lp-campo', rotulo: '', caixa: '', botao: 'lp-btn lp-btn--ouro',
    elo: 'lp-elo', erro: 'lp-aviso', nota: 'lp-aviso lp-aviso--nota', miudo: 'lp-miudo'
  }
} as const;

const ESPERA_S = 60;

/**
 * O passo do código (techspec-confirmacao-email): a conta foi criada, ou a senha conferiu, mas o e-mail ainda não foi
 * confirmado. O jogador digita os 6 números do e-mail, ou chega aqui pelo botão do e-mail com o código já preenchido.
 */
export function ConfirmarEmail({ variante, codigoInicial = '', serial }: {
  variante: keyof typeof CLASSES;
  codigoInicial?: string;
  serial?: string;
}) {
  const { confirmacao, confirmarEmail, reenviarCodigo, cancelarConfirmacao, busy, error, clearError } = useSession();
  const c = CLASSES[variante];
  const [codigo, setCodigo] = useState(codigoInicial.replace(/\D/g, '').slice(0, 6));
  const [espera, setEspera] = useState(ESPERA_S);
  const [reenviado, setReenviado] = useState(false);
  const campo = useRef<HTMLInputElement>(null);
  const enviouLink = useRef(false);

  // O relógio do "reenviar": o servidor também segura 60 s entre dois envios.
  useEffect(() => {
    if (espera <= 0) return;
    const id = window.setTimeout(() => setEspera(s => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [espera]);

  // Veio pelo botão do e-mail: confere sozinho, uma vez.
  useEffect(() => {
    if (codigo.length === 6 && codigoInicial && !enviouLink.current) {
      enviouLink.current = true;
      void confirmarEmail(codigo, serial);
    } else {
      campo.current?.focus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!confirmacao) return null;

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (busy || codigo.length !== 6) return;
    void confirmarEmail(codigo, serial);
  };

  const reenviar = () => {
    if (espera > 0 || busy) return;
    setReenviado(false);
    setEspera(ESPERA_S);
    void reenviarCodigo().then(() => setReenviado(true), () => {});
  };

  const input = (
    <input
      ref={campo}
      id="codigo-email"
      name="codigo"
      value={codigo}
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]{6}"
      maxLength={6}
      placeholder="000000"
      spellCheck={false}
      disabled={busy}
      aria-describedby={error ? 'codigo-email-erro' : undefined}
      onChange={e => { setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6)); clearError(); }}
      style={{ letterSpacing: '.35em', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
    />
  );

  return (
    <form className={c.form} onSubmit={enviar} noValidate>
      <p className={c.miudo} role="status">{t('confirmar.texto', { nome: confirmacao })}</p>
      <div className={c.campo}>
        {c.rotulo
          ? <span className={c.rotulo}><label htmlFor="codigo-email">{t('confirmar.codigo')}</label></span>
          : <label htmlFor="codigo-email">{t('confirmar.codigo')}</label>}
        {c.caixa ? <span className={c.caixa}>{input}</span> : input}
      </div>
      {error && <p className={c.erro} id="codigo-email-erro" role="alert">{error}</p>}
      {reenviado && !error && <p className={c.nota} role="status">{t('confirmar.reenviado')}</p>}
      <button type="submit" className={c.botao} disabled={busy || codigo.length !== 6}>
        {busy ? t('confirmar.confirmando') : t('confirmar.enviar')}
      </button>
      <p className={c.miudo}>{t('confirmar.dica')}</p>
      <p className={c.miudo}>
        <button type="button" className={c.elo} onClick={reenviar} disabled={espera > 0 || busy}>
          {espera > 0 ? t('confirmar.reenviarEm', { n: espera }) : t('confirmar.reenviar')}
        </button>
        {' · '}
        <button type="button" className={c.elo} onClick={cancelarConfirmacao} disabled={busy}>
          {t('confirmar.outroEmail')}
        </button>
      </p>
    </form>
  );
}
